import { useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor, BubbleMenu, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered, Quote, Link2, Heading1, Heading2, Code, Undo2, Redo2, Sparkles,
  Minimize2, Maximize2, Briefcase, Smile, SpellCheck2, AlignLeft, AlignCenter, AlignRight, Baseline, Highlighter, ImagePlus, RemoveFormatting,
  Minus, CodeXml, Eye,
} from 'lucide-react';
import { renderEmail } from '@/lib/email-html';
import { useUi } from '@/lib/store';
import { askText } from './Dialogs';
import { cn } from './ui';

export const REWRITES = [
  { label: 'Shorter', instruction: 'make it noticeably shorter and tighter', icon: <Minimize2 /> },
  { label: 'Longer', instruction: 'expand it with a bit more helpful detail', icon: <Maximize2 /> },
  { label: 'Formal', instruction: 'make it more formal and polished', icon: <Briefcase /> },
  { label: 'Friendly', instruction: 'make it warmer and friendlier', icon: <Smile /> },
  { label: 'Fix grammar', instruction: 'fix spelling, grammar and punctuation only; keep wording otherwise', icon: <SpellCheck2 /> },
];

const COLORS = ['#111827', '#6b7280', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0891b2', '#2563eb', '#7c3aed', '#db2777'];
const HIGHLIGHTS = ['#fef08a', '#bbf7d0', '#bae6fd', '#e9d5ff', '#fecdd3'];
const MAX_INLINE_IMAGE = 3 * 1024 * 1024;

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

async function insertImageFiles(editor: Editor, files: File[]): Promise<boolean> {
  const images = files.filter((f) => f.type.startsWith('image/') && f.size <= MAX_INLINE_IMAGE);
  for (const f of images) editor.chain().focus().setImage({ src: await readAsDataUrl(f), alt: f.name }).run();
  return images.length > 0;
}

export function RichEditor({
  html, rev, onChange, onReady, streaming, placeholder, onRewriteSelection, minHeight,
}: {
  html: string;
  rev: number;
  onChange: (html: string) => void;
  onReady?: (e: Editor) => void;
  streaming?: boolean;
  placeholder?: string;
  onRewriteSelection?: (selection: string, instruction: string) => void;
  minHeight?: number;
}) {
  const editorRef = useRef<Editor | null>(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image.configure({ inline: true, allowBase64: true, HTMLAttributes: { style: 'max-width:100%;height:auto' } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer' } }),
      Placeholder.configure({ placeholder: placeholder ?? 'Write your message…' }),
    ],
    content: html,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: { spellcheck: 'true' },
      // Pasted / dropped images become inline images (sent as embedded cid: parts).
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []);
        if (!files.some((f) => f.type.startsWith('image/')) || !editorRef.current) return false;
        void insertImageFiles(editorRef.current, files);
        return true;
      },
      handleDrop: (_view, event) => {
        const files = Array.from((event as DragEvent).dataTransfer?.files ?? []);
        if (!files.some((f) => f.type.startsWith('image/')) || !editorRef.current) return false;
        event.preventDefault();
        event.stopPropagation();
        void insertImageFiles(editorRef.current, files);
        return true;
      },
    },
  });
  editorRef.current = editor;

  useEffect(() => {
    if (editor) onReady?.(editor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // External content replacement (undo-send restore, draft load, leaving HTML source mode).
  useEffect(() => {
    if (editor && rev > 0 && editor.getHTML() !== html) editor.commands.setContent(html, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rev]);

  useEffect(() => {
    editor?.setEditable(!streaming);
  }, [editor, streaming]);

  if (!editor) return null;

  const selectedText = () => {
    const { from, to } = editor.state.selection;
    return editor.state.doc.textBetween(from, to, '\n');
  };

  return (
    <div className={cn('dts-editor flex min-h-0 flex-1 flex-col', streaming && 'streaming')}>
      <BubbleMenu editor={editor} shouldShow={({ editor: e, from, to }) => from !== to && !e.isActive('image')} tippyOptions={{ duration: 120, maxWidth: 'none' }} className="glass flex items-center gap-0.5 rounded-xl bg-raised/95 p-1 shadow-2xl">
        {onRewriteSelection && (
          <>
            {REWRITES.map((r) => (
              <button
                key={r.label}
                onClick={() => onRewriteSelection(selectedText(), r.instruction)}
                className="flex h-7 items-center gap-1 rounded-lg px-2 text-[12px] font-medium text-fg transition hover:bg-accent/15 hover:text-accent [&_svg]:size-3.5"
              >
                {r.icon}
                {r.label}
              </button>
            ))}
            <button
              onClick={async () => {
                const sel = selectedText();
                const how = await askText('Rewrite selection', { placeholder: 'e.g. make this sound more confident', confirmLabel: 'Rewrite' });
                if (how) onRewriteSelection(sel, how);
              }}
              className="flex h-7 items-center gap-1 rounded-lg px-2 text-[12px] font-semibold transition hover:bg-accent/15"
            >
              <Sparkles className="size-3.5 text-accent" />
              <span className="ai-text">Ask AI</span>
            </button>
            <span className="mx-1 h-4 w-px bg-line" />
          </>
        )}
        <ToolButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} label="Bold"><Bold /></ToolButton>
        <ToolButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italic"><Italic /></ToolButton>
        <ToolButton active={editor.isActive('link')} onClick={() => void setLink(editor)} label="Link"><Link2 /></ToolButton>
      </BubbleMenu>
      <div className="min-h-0 flex-1 cursor-text overflow-y-auto px-5 py-3" style={{ minHeight }} onClick={() => !editor.isFocused && editor.commands.focus()}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

async function setLink(editor: Editor) {
  const prev = editor.getAttributes('link').href as string | undefined;
  const url = await askText('Link URL', { initial: prev ?? 'https://', confirmLabel: 'Apply' });
  if (url === null) return;
  if (!url || url === 'https://') editor.chain().focus().unsetLink().run();
  else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
}

/** Full formatting toolbar for the WYSIWYG mode. */
export function EditorToolbar({ editor, mode, onModeChange }: { editor: Editor | null; mode?: 'rich' | 'html'; onModeChange?: (m: 'rich' | 'html') => void }) {
  const [palette, setPalette] = useState<'color' | 'highlight' | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const rich = mode !== 'html';
  return (
    <div className="relative flex flex-wrap items-center gap-0.5">
      {rich && editor && (
        <>
          <ToolButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} label="Bold (Ctrl+B)"><Bold /></ToolButton>
          <ToolButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italic (Ctrl+I)"><Italic /></ToolButton>
          <ToolButton active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} label="Underline (Ctrl+U)"><UnderlineIcon /></ToolButton>
          <ToolButton active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} label="Strikethrough"><Strikethrough /></ToolButton>
          <ToolButton active={palette === 'color'} onClick={() => setPalette(palette === 'color' ? null : 'color')} label="Text color">
            <Baseline style={{ color: (editor.getAttributes('textStyle').color as string) || undefined }} />
          </ToolButton>
          <ToolButton active={editor.isActive('highlight')} onClick={() => setPalette(palette === 'highlight' ? null : 'highlight')} label="Highlight"><Highlighter /></ToolButton>
          <span className="mx-1 h-4 w-px bg-line" />
          <ToolButton active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} label="Title"><Heading1 /></ToolButton>
          <ToolButton active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} label="Heading"><Heading2 /></ToolButton>
          <ToolButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Bulleted list"><List /></ToolButton>
          <ToolButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Numbered list"><ListOrdered /></ToolButton>
          <ToolButton active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} label="Quote"><Quote /></ToolButton>
          <ToolButton active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} label="Inline code"><Code /></ToolButton>
          <span className="mx-1 h-4 w-px bg-line" />
          <ToolButton active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} label="Align left"><AlignLeft /></ToolButton>
          <ToolButton active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} label="Center"><AlignCenter /></ToolButton>
          <ToolButton active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} label="Align right"><AlignRight /></ToolButton>
          <span className="mx-1 h-4 w-px bg-line" />
          <ToolButton active={editor.isActive('link')} onClick={() => void setLink(editor)} label="Link"><Link2 /></ToolButton>
          <ToolButton onClick={() => fileInput.current?.click()} label="Insert image (or paste / drop one)"><ImagePlus /></ToolButton>
          <ToolButton onClick={() => editor.chain().focus().setHorizontalRule().run()} label="Divider"><Minus /></ToolButton>
          <ToolButton onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} label="Clear formatting"><RemoveFormatting /></ToolButton>
          <span className="mx-1 h-4 w-px bg-line" />
          <ToolButton onClick={() => editor.chain().focus().undo().run()} label="Undo"><Undo2 /></ToolButton>
          <ToolButton onClick={() => editor.chain().focus().redo().run()} label="Redo"><Redo2 /></ToolButton>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              if (e.target.files) void insertImageFiles(editor, Array.from(e.target.files));
              e.target.value = '';
            }}
          />
        </>
      )}
      {onModeChange && (
        <div className="ml-auto flex rounded-lg bg-line p-0.5">
          <button onClick={() => onModeChange('rich')} className={cn('flex h-6 items-center gap-1 rounded-md px-2 text-[11.5px] font-medium', rich ? 'bg-raised text-fg shadow-sm' : 'text-muted')}>
            <Eye className="size-3.5" /> Visual
          </button>
          <button onClick={() => onModeChange('html')} className={cn('flex h-6 items-center gap-1 rounded-md px-2 text-[11.5px] font-medium', !rich ? 'bg-raised text-fg shadow-sm' : 'text-muted')}>
            <CodeXml className="size-3.5" /> HTML
          </button>
        </div>
      )}
      {palette && editor && rich && (
        <div className="glass absolute left-24 top-full z-30 mt-1 flex gap-1 rounded-xl bg-raised/95 p-1.5 shadow-xl" onMouseLeave={() => setPalette(null)}>
          {(palette === 'color' ? COLORS : HIGHLIGHTS).map((c) => (
            <button
              key={c}
              title={c}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (palette === 'color') editor.chain().focus().setColor(c).run();
                else editor.chain().focus().toggleHighlight({ color: c }).run();
                setPalette(null);
              }}
              className="size-6 rounded-md border border-line-strong transition hover:scale-110"
              style={{ background: c }}
            />
          ))}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (palette === 'color') editor.chain().focus().unsetColor().run();
              else editor.chain().focus().unsetHighlight().run();
              setPalette(null);
            }}
            className="h-6 rounded-md px-2 text-[11px] text-muted hover:bg-line"
          >
            None
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Raw HTML source editor with a live, sandboxed preview. What you write here is sent exactly as written
 * (the visual editor would normalise unsupported markup such as tables and custom styles).
 */
export function HtmlSourceEditor({ html, onChange, minHeight = 220 }: { html: string; onChange: (html: string) => void; minHeight?: number }) {
  const theme = useUi((s) => s.local.theme);
  const [debounced, setDebounced] = useState(html);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(html), 250);
    return () => clearTimeout(t);
  }, [html]);
  const preview = useMemo(() => renderEmail(debounced, undefined, { allowRemote: true, dark: false, inline: {} }).srcdoc, [debounced]);
  return (
    <div className="grid min-h-0 flex-1 grid-cols-2 gap-0 border-t border-line" style={{ minHeight }}>
      <textarea
        value={html}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Tab') {
            e.preventDefault();
            const el = e.currentTarget;
            const { selectionStart: s, selectionEnd: end } = el;
            const next = html.slice(0, s) + '  ' + html.slice(end);
            onChange(next);
            requestAnimationFrame(() => el.setSelectionRange(s + 2, s + 2));
          }
        }}
        spellCheck={false}
        placeholder="<p>Write or paste HTML…</p>"
        className={cn('selectable h-full resize-none border-r border-line bg-bg/50 p-4 font-mono text-[12.5px] leading-relaxed outline-none', theme === 'light' ? 'text-[#1e1b4b]' : 'text-[#c4b5fd]')}
      />
      <div className="relative h-full overflow-hidden bg-white">
        <span className="absolute right-2 top-2 z-10 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">Preview</span>
        <iframe title="HTML preview" sandbox="allow-same-origin" srcDoc={preview} className="h-full w-full border-0" />
      </div>
    </div>
  );
}

function ToolButton({ active, onClick, label, children }: { active?: boolean; onClick: () => void; label: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn('flex size-7 items-center justify-center rounded-lg transition [&_svg]:size-[15px]', active ? 'bg-accent/15 text-accent' : 'text-muted hover:bg-line hover:text-fg')}
    >
      {children}
    </button>
  );
}
